import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Sparkles, Zap, Palette, Rocket, ArrowRight, Code2, Wand2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

export default function LandingPage() {
  const { user } = useAuthStore()

  return (
    <div className="min-h-screen bg-midnight-950 overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-ember-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-ember-400/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-ember-500/5 to-transparent rounded-full" />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-ember-400 to-ember-600 flex items-center justify-center glow-ember">
              <Sparkles className="w-5 h-5 text-midnight-950" />
            </div>
            <span className="font-display text-xl font-semibold text-white">Lumina</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-midnight-300 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="text-midnight-300 hover:text-white transition-colors">How it Works</a>
            <a href="#pricing" className="text-midnight-300 hover:text-white transition-colors">Pricing</a>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <Link
                to="/dashboard"
                className="px-5 py-2.5 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-lg hover:shadow-lg hover:shadow-ember-500/25 transition-all btn-primary"
              >
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="px-4 py-2 text-midnight-200 hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth?mode=signup"
                  className="px-5 py-2.5 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-lg hover:shadow-lg hover:shadow-ember-500/25 transition-all btn-primary"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 pt-20 pb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
              <Wand2 className="w-4 h-4 text-ember-400" />
              <span className="text-sm text-midnight-200">AI-Powered Website Generation</span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Build stunning websites
              <br />
              <span className="gradient-text">with simple prompts</span>
            </h1>

            <p className="text-lg md:text-xl text-midnight-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              Transform your ideas into beautiful, production-ready websites in seconds. 
              No coding required. Just describe what you want, and let AI do the rest.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={user ? "/editor" : "/auth?mode=signup"}
                className="group flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-xl hover:shadow-xl hover:shadow-ember-500/30 transition-all btn-primary text-lg"
              >
                Start Creating Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#how-it-works"
                className="flex items-center gap-2 px-8 py-4 text-white font-medium rounded-xl border border-midnight-700 hover:border-midnight-500 transition-colors"
              >
                See How It Works
              </a>
            </div>
          </motion.div>

          {/* Hero Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 relative"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-midnight-950 via-transparent to-transparent z-10 pointer-events-none" />
            <div className="gradient-border rounded-2xl overflow-hidden glow-ember-strong">
              <div className="bg-midnight-900/90 p-1">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-midnight-900 rounded-t-xl">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-midnight-800 rounded-lg text-sm text-midnight-400">
                      <Code2 className="w-4 h-4" />
                      lumina.ai/editor
                    </div>
                  </div>
                </div>
                {/* Editor preview */}
                <div className="bg-midnight-950 h-[400px] md:h-[500px] flex">
                  <div className="w-80 border-r border-midnight-800 p-4 hidden lg:block">
                    <div className="space-y-4">
                      <div className="h-10 bg-midnight-800/50 rounded-lg shimmer" />
                      <div className="h-32 bg-midnight-800/30 rounded-lg" />
                      <div className="h-8 bg-ember-500/20 rounded-lg w-2/3" />
                    </div>
                  </div>
                  <div className="flex-1 p-6">
                    <div className="h-full bg-gradient-to-br from-midnight-900 to-midnight-950 rounded-xl border border-midnight-800 flex items-center justify-center">
                      <div className="text-center">
                        <Sparkles className="w-16 h-16 text-ember-400 mx-auto mb-4 animate-pulse-slow" />
                        <p className="text-midnight-400 font-mono text-sm">
                          "Create a modern SaaS landing page with pricing..."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 px-6 py-24 bg-midnight-900/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
              Everything you need to build
              <br />
              <span className="gradient-text">beautiful websites</span>
            </h2>
            <p className="text-midnight-300 text-lg max-w-2xl mx-auto">
              Powerful AI features combined with an intuitive interface make website creation effortless.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: 'Instant Generation',
                description: 'Generate complete websites in seconds. From concept to code in one click.',
              },
              {
                icon: Palette,
                title: 'Beautiful Designs',
                description: 'Modern, responsive designs that look stunning on every device.',
              },
              {
                icon: Code2,
                title: 'Clean Code',
                description: 'Export production-ready HTML, CSS, and JavaScript you can customize.',
              },
              {
                icon: Wand2,
                title: 'AI Refinement',
                description: 'Iteratively improve your design with natural language commands.',
              },
              {
                icon: Rocket,
                title: 'One-Click Deploy',
                description: 'Deploy directly to Cloudflare, Vercel, or Netlify with a single click.',
              },
              {
                icon: Sparkles,
                title: 'Smart Templates',
                description: 'Start with AI-curated templates optimized for conversion.',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 rounded-2xl bg-midnight-900/50 border border-midnight-800 hover:border-ember-500/30 transition-all hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ember-400/20 to-ember-600/10 flex items-center justify-center mb-4 group-hover:from-ember-400/30 group-hover:to-ember-600/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-ember-400" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2 text-white">{feature.title}</h3>
                <p className="text-midnight-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
              Three steps to your
              <br />
              <span className="gradient-text">dream website</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Describe Your Vision',
                description: 'Tell Lumina what you want to build. Be as detailed or simple as you like.',
              },
              {
                step: '02',
                title: 'Watch AI Create',
                description: 'Our AI generates a beautiful, responsive website based on your description.',
              },
              {
                step: '03',
                title: 'Refine & Deploy',
                description: 'Tweak the design with more prompts, then deploy with one click.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="text-7xl font-display font-bold text-midnight-800 mb-4">{item.step}</div>
                <h3 className="font-display text-2xl font-semibold mb-3 text-white">{item.title}</h3>
                <p className="text-midnight-400 leading-relaxed">{item.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 -right-4 text-midnight-700">
                    <ArrowRight className="w-8 h-8" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-ember-500/20 to-ember-600/10" />
            <div className="absolute inset-0 bg-midnight-900/80 backdrop-blur-xl" />
            <div className="relative px-8 py-16 md:px-16 text-center">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 text-white">
                Ready to build something amazing?
              </h2>
              <p className="text-midnight-300 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of creators using Lumina to bring their website ideas to life.
              </p>
              <Link
                to={user ? "/dashboard" : "/auth?mode=signup"}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-xl hover:shadow-xl hover:shadow-ember-500/30 transition-all btn-primary text-lg"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 border-t border-midnight-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-ember-400 to-ember-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-midnight-950" />
            </div>
            <span className="font-display font-semibold text-white">Lumina</span>
          </div>
          <p className="text-midnight-500 text-sm">
            © {new Date().getFullYear()} Lumina AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}

